import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Menu, Search, User, LogOut, ChevronDown, Heart } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ROUTES } from '@/lib/constants';
import { useAuth } from '@/hooks/useAuth';

const navLinks = [
  { href: ROUTES.farms, label: 'Wine Farms' },
  { href: ROUTES.regions, label: 'Regions' },
  { href: ROUTES.map, label: 'Map' },
  { href: ROUTES.routePlanner, label: 'Plan Route' },
];

export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, profile, signOut, isLoading } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setDropdownOpen(false);
    signOut();
    navigate(ROUTES.home);
  };

  // Get display name or email for the user menu
  const displayName = profile?.displayName || user?.email?.split('@')[0] || 'User';

  return (
    <header className="hidden md:block sticky top-0 z-sticky bg-white border-b border-neutral-200">
      <div className="container-app">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={ROUTES.home} className="flex items-center gap-2">
            <span className="font-display text-xl font-bold">
              <span className="text-wine">Grape</span>
              <span className="text-gold">Adventures</span>
            </span>
          </Link>

          {/* Navigation */}
          <nav className="flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  location.pathname === link.href
                    ? 'bg-wine/10 text-wine'
                    : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="p-2">
              <Search className="w-5 h-5" />
            </Button>

            {isLoading ? (
              // Loading state
              <div className="w-24 h-9 bg-neutral-100 animate-pulse rounded-lg" />
            ) : isAuthenticated ? (
              // Authenticated - show user dropdown
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    dropdownOpen
                      ? 'bg-wine/10 text-wine'
                      : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
                  )}
                >
                  <div className="w-7 h-7 rounded-full bg-wine/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-wine" />
                  </div>
                  <span className="max-w-[120px] truncate">{displayName}</span>
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 transition-transform',
                      dropdownOpen && 'rotate-180'
                    )}
                  />
                </button>

                {/* Dropdown menu */}
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-50">
                    <Link
                      to={ROUTES.profile}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                    >
                      <User className="w-4 h-4" />
                      My Profile
                    </Link>
                    <Link
                      to={ROUTES.myLists}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                    >
                      <Heart className="w-4 h-4" />
                      My Lists
                    </Link>
                    <hr className="my-1 border-neutral-200" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // Not authenticated - show login/signup buttons
              <div className="flex items-center gap-2">
                <Link to={ROUTES.login}>
                  <Button variant="ghost" size="sm">
                    Sign In
                  </Button>
                </Link>
                <Link to={ROUTES.signup}>
                  <Button variant="primary" size="sm">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

// Mobile header (simpler)
export function MobileHeader() {
  const { isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    setMenuOpen(false);
    signOut();
    navigate(ROUTES.home);
  };

  return (
    <header className="md:hidden sticky top-0 z-sticky bg-white border-b border-neutral-200">
      <div className="flex items-center justify-between h-14 px-4">
        <button
          className="p-2 -ml-2 rounded-lg hover:bg-neutral-100"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <Menu className="w-5 h-5" />
        </button>
        <Link to={ROUTES.home} className="font-display text-lg font-bold">
          <span className="text-wine">Grape</span>
          <span className="text-gold">Adventures</span>
        </Link>
        {isAuthenticated ? (
          <Link to={ROUTES.profile} className="p-2 -mr-2 rounded-lg hover:bg-neutral-100">
            <User className="w-5 h-5" />
          </Link>
        ) : (
          <Link to={ROUTES.login} className="p-2 -mr-2 rounded-lg hover:bg-neutral-100">
            <User className="w-5 h-5" />
          </Link>
        )}
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="absolute top-14 left-0 right-0 bg-white border-b border-neutral-200 shadow-lg z-40">
          <nav className="flex flex-col py-2">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMenuOpen(false)}
                className="px-4 py-3 text-neutral-700 hover:bg-neutral-50"
              >
                {link.label}
              </Link>
            ))}
            <hr className="my-2 border-neutral-200" />
            {isAuthenticated ? (
              <>
                <Link
                  to={ROUTES.profile}
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-3 text-neutral-700 hover:bg-neutral-50"
                >
                  My Profile
                </Link>
                <Link
                  to={ROUTES.myLists}
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-3 text-neutral-700 hover:bg-neutral-50"
                >
                  My Lists
                </Link>
                <button
                  onClick={handleLogout}
                  className="px-4 py-3 text-left text-red-600 hover:bg-red-50"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to={ROUTES.login}
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-3 text-neutral-700 hover:bg-neutral-50"
                >
                  Sign In
                </Link>
                <Link
                  to={ROUTES.signup}
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-3 text-wine font-medium hover:bg-neutral-50"
                >
                  Sign Up
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
